import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Separator } from './ui/separator';
import { Plus, Trash2, Receipt, Download, FileText } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const InvoiceModal = ({ open, onClose, workcard, onSuccess }) => {
  const [lineItems, setLineItems] = useState([
    { description: '', quantity: 1, unit_price: 0, total: 0 }
  ]);
  const [issueDate, setIssueDate] = useState(new Date().toISOString().split('T')[0]);
  const [deliveryDate, setDeliveryDate] = useState('');
  const [paymentDays, setPaymentDays] = useState(30);
  const [vatRate, setVatRate] = useState(25);
  const [notes, setNotes] = useState('');
  const [isCreating, setIsCreating] = useState(false);
  const [customer, setCustomer] = useState(null);

  useEffect(() => {
    if (workcard && open) {
      fetchCustomer();
      // Pre-fill with task description
      setLineItems([
        { description: workcard.task_description || '', quantity: 1, unit_price: 0, total: 0 }
      ]);
    }
  }, [workcard, open]);

  const fetchCustomer = async () => {
    if (!workcard?.customer_id) return;
    try {
      const response = await axios.get(`${API}/customers/${workcard.customer_id}`);
      setCustomer(response.data);
    } catch (error) {
      console.error('Could not fetch customer:', error);
    }
  };

  const updateLineItem = (index, field, value) => {
    const newItems = [...lineItems];
    newItems[index][field] = value;
    
    // Auto-calculate total
    if (field === 'quantity' || field === 'unit_price') {
      newItems[index].total = parseFloat(newItems[index].quantity || 0) * parseFloat(newItems[index].unit_price || 0);
    }
    
    setLineItems(newItems);
  };

  const addLineItem = () => {
    setLineItems([...lineItems, { description: '', quantity: 1, unit_price: 0, total: 0 }]);
  };

  const removeLineItem = (index) => {
    if (lineItems.length === 1) return;
    setLineItems(lineItems.filter((_, i) => i !== index));
  };

  const calculateSubtotal = () => {
    return lineItems.reduce((sum, item) => sum + (parseFloat(item.total) || 0), 0);
  };

  const calculateVat = () => {
    return calculateSubtotal() * (vatRate / 100);
  };

  const calculateTotal = () => {
    return calculateSubtotal() + calculateVat();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate
    const validItems = lineItems.filter(item => item.description && item.total > 0);
    if (validItems.length === 0) {
      toast.error('Tilføj mindst én ydelse med en pris');
      return;
    }

    setIsCreating(true);
    try {
      const response = await axios.post(`${API}/invoices`, {
        workcard_id: workcard.id,
        line_items: validItems.map(item => ({
          description: item.description,
          quantity: parseFloat(item.quantity) || 1,
          unit_price: parseFloat(item.unit_price) || 0,
          total: parseFloat(item.total) || 0
        })),
        issue_date: issueDate,
        delivery_date: deliveryDate || null,
        payment_days: parseInt(paymentDays),
        vat_rate: parseFloat(vatRate),
        notes: notes || null
      });

      // Auto-download PDF
      try {
        const pdfResponse = await axios.get(`${API}/invoices/${response.data.id}/pdf`, {
          responseType: 'blob'
        });
        const url = window.URL.createObjectURL(new Blob([pdfResponse.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `faktura_${response.data.invoice_number}.pdf`);
        document.body.appendChild(link);
        link.click();
        link.remove();
      } catch (pdfError) {
        console.error('Could not download PDF:', pdfError);
      }

      onSuccess();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Kunne ikke oprette faktura');
    } finally {
      setIsCreating(false);
    }
  };

  const handleClose = () => {
    setLineItems([{ description: '', quantity: 1, unit_price: 0, total: 0 }]);
    setIssueDate(new Date().toISOString().split('T')[0]);
    setDeliveryDate('');
    setPaymentDays(30);
    setVatRate(25);
    setNotes('');
    onClose();
  };

  if (!workcard) return null;

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="invoice-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Receipt className="h-5 w-5" />
            Opret Faktura
          </DialogTitle>
          <DialogDescription>
            Arbejdskort #{workcard.card_number} - {workcard.customer_name}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Company Info (Read-only) */}
          <div className="p-4 bg-muted/50 rounded-lg">
            <h4 className="font-semibold mb-2">Sælger</h4>
            <div className="text-sm space-y-1 text-muted-foreground">
              <p className="font-medium text-foreground">ODINR WEB SERVICES</p>
              <p>KARINEVEJ 10, 6800 VARDE</p>
              <p>CVR: HOBBYVIRKSOMHED</p>
              <p>PELLE LAURSEN | prl@odinr.dk | odinr.dk</p>
            </div>
          </div>

          {/* Customer Info */}
          <div className="p-4 bg-muted/50 rounded-lg">
            <h4 className="font-semibold mb-2">Køber</h4>
            <div className="text-sm space-y-1">
              <p className="font-medium">{customer?.name || workcard.customer_name}</p>
              <p className="text-muted-foreground">{customer?.address || ''}</p>
              <p className="text-muted-foreground">{customer?.email || ''}</p>
            </div>
          </div>

          {/* Dates */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Udstedelsesdato</Label>
              <Input
                type="date"
                value={issueDate}
                onChange={(e) => setIssueDate(e.target.value)}
                data-testid="invoice-issue-date"
              />
            </div>
            <div className="space-y-2">
              <Label>Leveringsdato (valgfri)</Label>
              <Input
                type="date"
                value={deliveryDate}
                onChange={(e) => setDeliveryDate(e.target.value)}
                data-testid="invoice-delivery-date"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Betalingsfrist (dage)</Label>
              <Input
                type="number"
                value={paymentDays}
                onChange={(e) => setPaymentDays(e.target.value)}
                min="1"
                data-testid="invoice-payment-days"
              />
            </div>
            <div className="space-y-2">
              <Label>Momssats (%)</Label>
              <Input
                type="number"
                value={vatRate}
                onChange={(e) => setVatRate(e.target.value)}
                min="0"
                max="100"
                data-testid="invoice-vat-rate"
              />
            </div>
          </div>

          <Separator />

          {/* Line Items */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <Label className="text-base">Ydelser / Varer</Label>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={addLineItem}
                data-testid="add-line-item-btn"
              >
                <Plus className="h-4 w-4 mr-1" />
                Tilføj
              </Button>
            </div>

            {lineItems.map((item, index) => (
              <div key={index} className="grid grid-cols-12 gap-2 items-end">
                <div className="col-span-5">
                  {index === 0 && <Label className="text-xs">Beskrivelse</Label>}
                  <Input
                    placeholder="F.eks. Komplet Hjemmeside"
                    value={item.description}
                    onChange={(e) => updateLineItem(index, 'description', e.target.value)}
                    data-testid={`line-item-desc-${index}`}
                  />
                </div>
                <div className="col-span-2">
                  {index === 0 && <Label className="text-xs">Antal</Label>}
                  <Input
                    type="number"
                    min="1"
                    value={item.quantity}
                    onChange={(e) => updateLineItem(index, 'quantity', e.target.value)}
                    data-testid={`line-item-qty-${index}`}
                  />
                </div>
                <div className="col-span-2">
                  {index === 0 && <Label className="text-xs">Enhedspris</Label>}
                  <Input
                    type="number"
                    min="0"
                    placeholder="0"
                    value={item.unit_price}
                    onChange={(e) => updateLineItem(index, 'unit_price', e.target.value)}
                    data-testid={`line-item-price-${index}`}
                  />
                </div>
                <div className="col-span-2">
                  {index === 0 && <Label className="text-xs">Total</Label>}
                  <Input
                    type="number"
                    value={item.total}
                    onChange={(e) => updateLineItem(index, 'total', e.target.value)}
                    data-testid={`line-item-total-${index}`}
                  />
                </div>
                <div className="col-span-1">
                  {lineItems.length > 1 && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      onClick={() => removeLineItem(index)}
                      className="text-destructive"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* Notes */}
          <div className="space-y-2">
            <Label>Bemærkninger (valgfri)</Label>
            <Input
              placeholder="Evt. bemærkninger til fakturaen..."
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              data-testid="invoice-notes"
            />
          </div>

          <Separator />

          {/* Totals */}
          <div className="space-y-2 text-right">
            <div className="flex justify-between">
              <span className="text-muted-foreground">Subtotal (ekskl. moms):</span>
              <span className="font-medium">{calculateSubtotal().toFixed(2)} kr</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Moms ({vatRate}%):</span>
              <span className="font-medium">{calculateVat().toFixed(2)} kr</span>
            </div>
            <div className="flex justify-between text-lg font-bold">
              <span>Total (inkl. moms):</span>
              <span>{calculateTotal().toFixed(2)} kr</span>
            </div>
          </div>

          {/* Actions */}
          <div className="flex gap-2 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
              className="flex-1"
            >
              Annuller
            </Button>
            <Button
              type="submit"
              disabled={isCreating}
              className="flex-1"
              data-testid="create-invoice-btn"
            >
              {isCreating ? (
                <span className="flex items-center gap-2">
                  <span className="animate-spin h-4 w-4 border-2 border-current border-t-transparent rounded-full" />
                  Opretter...
                </span>
              ) : (
                <span className="flex items-center gap-2">
                  <FileText className="h-4 w-4" />
                  Opret & Download PDF
                </span>
              )}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default InvoiceModal;
